import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FinalCTA from '@/components/FinalCTA';
import ScrollToTop from '@/components/ScrollToTop';
import SEO from '@/components/SEO';
import { Check, X, Minus, Shield, Users, Code, Globe, Eye, Lock } from 'lucide-react';

const ComparisonCell = ({ value }: { value: string }) => {
  if (value === '✓') {
    return <Check className="h-5 w-5 text-green-400 mx-auto" />;
  }
  if (value === '✗') {
    return <X className="h-5 w-5 text-gray-500 mx-auto" />;
  }
  return <span className="text-yellow-400 text-sm">{value}</span>;
};

const comparisonData = [
  { capability: 'Plain English Tests', skytest: '✓', testrigor: '✓', qawolf: '✗', octomind: 'Partial', mabl: 'Partial' },
  { capability: 'Simultaneous Multi-Browser Coordination', skytest: '✓', testrigor: '✗', qawolf: '✗', octomind: '✗', mabl: '✗' },
  { capability: 'Multi-Role User Flows', skytest: '✓', testrigor: 'Partial', qawolf: '✗', octomind: '✗', mabl: '✗' },
  { capability: 'Cloud-Hosted Platform', skytest: '✓', testrigor: '✓', qawolf: '✓', octomind: '✓', mabl: '✓' },
  { capability: 'Self-Hosted Option', skytest: '✓', testrigor: 'Enterprise', qawolf: '✗', octomind: 'Partial', mabl: '✗' },
  { capability: 'Open Source Core', skytest: '✓', testrigor: '✗', qawolf: '✗', octomind: '✗', mabl: '✗' },
  { capability: 'Screenshot Evidence Per Step', skytest: '✓', testrigor: 'Partial', qawolf: '✓', octomind: '✓', mabl: '✓' },
  { capability: 'Hybrid AI + Code Steps', skytest: '✓', testrigor: 'Partial', qawolf: 'N/A', octomind: '✓', mabl: 'Partial' },
  { capability: 'No Vendor Lock-in', skytest: '✓', testrigor: '✗', qawolf: 'Partial', octomind: '✓', mabl: '✗' },
];

const competitors = ['SkyTest', 'testRigor', 'QA Wolf', 'Octomind', 'mabl'];
const competitorKeys = ['skytest', 'testrigor', 'qawolf', 'octomind', 'mabl'] as const;

const Compare = () => {
  return (
    <div className="flex flex-col min-h-screen bg-dark-bg relative">
      <SEO
        title="Why SkyTest - Compare Test Automation Platforms | SkyTest.ai"
        description="See how SkyTest.ai compares to testRigor, QA Wolf, Octomind, and mabl. Open source test automation with cloud and self-hosted options and simultaneous multi-browser coordination."
      />
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,34,255,0.05)_0%,rgba(10,10,20,0)_70%)] pointer-events-none"></div>

      <Navbar />

      <main className="flex-grow relative">
        {/* Hero Section */}
        <section className="min-h-[50vh] relative flex items-center justify-center section-padding overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(to bottom, hsl(240, 80%, 7%), #0a0a14)',
              }}
            />
            <div
              className="absolute top-[20%] left-1/2 -translate-x-1/2 w-[60%] h-[40%] rounded-full blur-[100px]"
              style={{
                background: 'radial-gradient(circle at center, hsl(210, 80%, 50%) 0%, rgba(0,34,255,0.05) 70%)',
                opacity: 0.3,
                animation: 'pulse-subtle 8s ease-in-out infinite',
              }}
            />
          </div>

          <div className="container max-w-5xl mx-auto flex flex-col items-center text-center z-10">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight tracking-tight max-w-4xl animate-fade-up">
              <span className="text-gradient">Why Teams Choose SkyTest</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mb-10 animate-fade-up">
              The only open source test automation platform that coordinates multiple browsers simultaneously in a single test run. See how we compare.
            </p>
          </div>
        </section>

        {/* Comparison Table Section */}
        <section className="section-padding relative">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -bottom-20 -right-20 w-[40%] h-[40%] rounded-full bg-brand/5 blur-[150px] animate-pulse-subtle" />
          </div>

          <div className="container mx-auto">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center animate-fade-up">
                <span className="text-gradient">Feature Comparison</span>
              </h2>

              <div className="glass-morphism rounded-3xl p-2 md:p-6 animate-fade-up overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px]">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">Capability</th>
                        {competitors.map((name, i) => (
                          <th
                            key={name}
                            className={`py-4 px-4 text-center text-sm font-semibold ${
                              i === 0
                                ? 'text-blue-400 bg-blue-500/10 rounded-t-xl'
                                : 'text-gray-400 font-medium'
                            }`}
                          >
                            {name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonData.map((row, rowIndex) => (
                        <tr
                          key={row.capability}
                          className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                        >
                          <td className="py-4 px-4 text-gray-300 text-sm font-medium">{row.capability}</td>
                          {competitorKeys.map((key, i) => (
                            <td
                              key={key}
                              className={`py-4 px-4 text-center ${
                                i === 0 ? 'bg-blue-500/10' : ''
                              } ${rowIndex === comparisonData.length - 1 && i === 0 ? 'rounded-b-xl' : ''}`}
                            >
                              <ComparisonCell value={row[key]} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* vs testRigor */}
        <section className="section-padding relative">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-20 -left-20 w-[40%] h-[40%] rounded-full bg-brand/5 blur-[150px] animate-pulse-subtle" />
          </div>

          <div className="container mx-auto">
            <div className="max-w-5xl mx-auto space-y-8">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center animate-fade-up">
                <span className="text-gradient">Head-to-Head Comparisons</span>
              </h2>

              {/* vs testRigor Card */}
              <div className="glass-morphism p-8 md:p-10 rounded-3xl animate-fade-up">
                <h3 className="text-2xl font-bold mb-4 text-white">SkyTest vs testRigor</h3>
                <p className="text-gray-300 leading-relaxed mb-6">
                  Both platforms support plain English test authoring and multi-user flows. The difference: SkyTest coordinates multiple browsers simultaneously in a single test run. When a customer's checkout needs to trigger real-time updates in the admin portal, SkyTest runs both browsers side by side and validates the interaction as it happens.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Users className="h-5 w-5 text-blue-400 mt-0.5 shrink-0" />
                    <span className="text-gray-300">Simultaneous multi-browser coordination, not sequential user handoffs</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Shield className="h-5 w-5 text-blue-400 mt-0.5 shrink-0" />
                    <span className="text-gray-300">Open source core with cloud and self-hosted deployment options on every plan</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Code className="h-5 w-5 text-blue-400 mt-0.5 shrink-0" />
                    <span className="text-gray-300">Full hybrid AI + code steps — mix plain English with custom scripts in any test</span>
                  </li>
                </ul>
              </div>

              {/* vs QA Wolf Card */}
              <div className="glass-morphism p-8 md:p-10 rounded-3xl animate-fade-up" style={{ animationDelay: '0.1s' }}>
                <h3 className="text-2xl font-bold mb-4 text-white">SkyTest vs QA Wolf</h3>
                <p className="text-gray-300 leading-relaxed mb-6">
                  QA Wolf's primary offering is managed QA-as-a-service, typically $65-90K/year. They do offer a self-serve tier, and tests are written in portable Playwright code. SkyTest takes a different approach: a self-serve platform (cloud or self-hosted) with an open source core, so you own your test infrastructure from day one and scale on your own terms.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Globe className="h-5 w-5 text-blue-400 mt-0.5 shrink-0" />
                    <span className="text-gray-300">Self-serve platform designed for teams who want hands-on control</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Lock className="h-5 w-5 text-blue-400 mt-0.5 shrink-0" />
                    <span className="text-gray-300">Open source core — deploy on our cloud or your own infrastructure</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Users className="h-5 w-5 text-blue-400 mt-0.5 shrink-0" />
                    <span className="text-gray-300">Multi-browser coordinated testing included as a standard feature</span>
                  </li>
                </ul>
              </div>

              {/* vs Octomind Card */}
              <div className="glass-morphism p-8 md:p-10 rounded-3xl animate-fade-up" style={{ animationDelay: '0.2s' }}>
                <h3 className="text-2xl font-bold mb-4 text-white">SkyTest vs Octomind</h3>
                <p className="text-gray-300 leading-relaxed mb-6">
                  Octomind generates portable Playwright code from natural language prompts and supports local test execution. Where SkyTest differs: tests stay in plain English (no generated code to review), and you can coordinate multiple browsers simultaneously to test cross-role interactions in a single run.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Users className="h-5 w-5 text-blue-400 mt-0.5 shrink-0" />
                    <span className="text-gray-300">Simultaneous multi-browser coordination for cross-role scenarios</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Code className="h-5 w-5 text-blue-400 mt-0.5 shrink-0" />
                    <span className="text-gray-300">Tests stay in plain English — no Playwright code to read or maintain</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Shield className="h-5 w-5 text-blue-400 mt-0.5 shrink-0" />
                    <span className="text-gray-300">Open source core with full self-hosted deployment, not limited to local test runners</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <FinalCTA />
      </main>

      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default Compare;
